import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  firstValueFrom,
  Subscription as RxSubscription,
} from 'rxjs';
import { IExtraInfo, IExtraInfoOrder } from 'src/app/interfaces/extra-info';
import { IListingDataSourceConfig } from 'src/app/interfaces/listing-data-source-config';

import { HttpClient } from '@angular/common/http';
import { ProductInfoApiService } from './product-info-api-service';

export class ProductInfoDataSource extends MatTableDataSource<any> {
  public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private ProductInfo: BehaviorSubject<IExtraInfo[]> = new BehaviorSubject([]);
  private Subscriptions: RxSubscription[] = [];
  public uploadProgress: BehaviorSubject<number> = new BehaviorSubject(0);
  public uploadProgress$ = this.uploadProgress.asObservable();
  public uploading = new BehaviorSubject(false);

  constructor(
    protected DataSourceConfig: IListingDataSourceConfig,
    protected ProductInfoApi: ProductInfoApiService,
    private readonly Http: HttpClient,
  ) {
    super();
  }

  public payment() {
    return this.data.filter((item) => item.type === 'payment');
  }

  public product() {
    return this.data.filter((item) => item.type === 'product');
  }

  public getConfig() {
    return this.DataSourceConfig;
  }

  public load() {
    this.loading.next(true);

    this.ProductInfoApi.list(
      this.DataSourceConfig.role,
      this.DataSourceConfig.listingId,
    ).then(
      (ProductInfo: IExtraInfo[]) => {
        this.data = ProductInfo;
        this.ProductInfo.next(ProductInfo);
        this.loading.next(false);
      },
      (error) => this.loading.next(false),
    );
  }

  public list() {
    this.load();

    const ref = this.ProductInfoApi.viewList(this);

    this.Subscriptions.push(ref.afterClosed().subscribe(() => this.load()));

    return ref;
  }
  public async save(ProductInfo: IExtraInfo) {
    const { files = [], ...extraInfoRest } = ProductInfo as any;

    const extraInfo = {
      ...extraInfoRest,
      files,
    };

    this.loading.next(true);
    this.uploadProgress?.next(0);

    try {
      const hasNewFiles = files.some(
        (f) => f.src instanceof File || f.src instanceof Blob,
      );

      const result = await this.ProductInfoApi.save(
        this.DataSourceConfig.role,
        this.DataSourceConfig.listingId,
        extraInfo,
        hasNewFiles,
      );

      let didUpload = false;

      if (files.length > 0 && result.putUrls?.length) {
        let completed = 0;
        const total = result.putUrls.length;

        const uploadPromises = result.putUrls.map(({ putUrl, fileName }) => {
          const file = files.find((f) => f.name === fileName);

          if (
            !file ||
            !(file.src instanceof File || file.src instanceof Blob)
          ) {
            console.log(
              `Skipping upload for already uploaded or renamed file: ${fileName}`,
            );
            return Promise.resolve();
          }

          didUpload = true;

          return firstValueFrom(
            this.Http.put(putUrl, file.src, {
              headers: { 'Content-Type': file.type },
            }),
          )
            .then(() => {
              completed++;
              const progress = Math.round((completed / total) * 100);
              this.uploadProgress.next(progress);
              file.src = null; // Prevent re-upload
            })
            .catch((err) => {
              console.error('Upload failed for:', fileName, err);
            });
        });

        await Promise.all(uploadPromises);
        this.uploadProgress.next(0);
      }

      if (didUpload) {
        await this.ProductInfoApi.syncExtraInfo(
          this.DataSourceConfig.role,
          result.id,
        );
      }

      await this.ProductInfoApi.notify(extraInfo.title + ' saved.');
    } catch (e) {
      await this.ProductInfoApi.notify(e, false);
      this.uploadProgress?.next(0);
    } finally {
      this.loading.next(false);
      this.load();
    }
  }

  public setOrder(ProductInfos: IExtraInfoOrder[]) {
    this.loading.next(true);

    return this.ProductInfoApi.order(
      this.DataSourceConfig.role,
      this.DataSourceConfig.listingId,
      ProductInfos,
    ).then(() => this.load());
  }

  public delete(ProductInfo: IExtraInfo) {
    return new Promise((reject) => {
      this.ProductInfoApi.delete(
        this.DataSourceConfig.role,
        this.DataSourceConfig.listingId,
        ProductInfo,
      ).then(
        () => this.load(),
        (error) => reject(error),
      );
    });
  }

  // product-info-data-source.ts
  public deleteFile(extraInfoId, fileTitle): Promise<any> {
    return this.ProductInfoApi.deleteFileFromS3(
      this.DataSourceConfig.role,
      extraInfoId,
      fileTitle,
    );
  }

  /**
   * Connects a collection viewer (such as a data-table) to this data source.
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   * @returns Observable that emits a new value when the data changes.
   */
  public connect() {
    return this.ProductInfo;
  }

  /**
   * Disconnects a collection viewer (such as a data-table) from this data source. Can be used
   * to perform any clean-up or tear-down operations when a view is being destroyed.
   *
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   */
  public disconnect(collectionViewer?: CollectionViewer) {
    this.Subscriptions.forEach((Subscription) => Subscription.unsubscribe());
  }
}
